import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { makeRequest } from "../../../../../../utils/makeRequest";
import { makeRequest2 } from "../../../../../../utils/makeRequest";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import styles from "./invoice.module.css";
import {
  markInvoiceViewed,
  getLevyURL,
  getBankDetailsForLevyURL,
  getBillerAddressForLevyURL,
  getFacilityPaymentDetailsURL,
} from "../../../../../../utils/urls";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { toastify } from "../../../../../../utils/toast";
import EtimsQRCode from "../../../../../common/EtimsQRCode";

const UniversalInvoicePage = () => {
  // State management
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [facilityLogo, setFacilityLogo] = useState("");
  const [error, setError] = useState(null);

  // Payment details state (similar to invoice page)
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [billerAddress, setBillerAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [contractDetails, setContractDetails] = useState(null);

  // Individual loading states
  const [billerAddressLoading, setBillerAddressLoading] = useState(false);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [paymentSettingsLoading, setPaymentSettingsLoading] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);

  // Receipts dialog state
  const [showReceiptsDialog, setShowReceiptsDialog] = useState(false);

  // Zoom functionality
  const [zoomLevel, setZoomLevel] = useState(1);
  const invoiceViewerRef = useRef(null);

  // Min and max zoom levels
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 2;
  const ZOOM_STEP = 0.1;

  // Get URL parameters
  const { facilityId, invoiceId, type } = useParams();
  const navigate = useNavigate();

  // Check if all payment data has loaded
  const allPaymentDataLoaded =
    !contractLoading &&
    !billerAddressLoading &&
    !bankDetailsLoading &&
    !paymentSettingsLoading;

  useEffect(() => {
    if (facilityId && invoiceId) {
      fetchInvoiceData();
      fetchCompanyDetails();
      markInvoiceAsViewed();
    }
  }, [invoiceId, facilityId, type]);

  // Fetch payment details when invoice is loaded (similar to invoice page)
  useEffect(() => {
    if (invoice && invoice.whatFor) {
      if (
        invoice.whatFor.invoiceType === "Contract" &&
        invoice.whatFor.description
      ) {
        fetchPaymentDetailsForContract(invoice.whatFor.description);
      }
    }
  }, [invoice]);

  // Helper function to safely extract data from potentially double-wrapped responses
  const extractResponseData = (response) => {
    if (!response) return null;

    if (response.success && response.data) {
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return response.data;
    }

    return response;
  };

  const fetchLevyById = async (levyId) => {
    try {
      const response = await makeRequest2(
        `${getLevyURL}/${facilityId}/${levyId}`,
        "GET",
      );

      if (response.success) {
        const levyData = extractResponseData(response);
        return levyData;
      }
      return null;
    } catch (error) {
      console.error("Error fetching levy:", error);
      return null;
    }
  };

  const fetchPaymentDetailsForContract = async (contractId) => {
    try {
      setContractLoading(true);
      setBillerAddressLoading(true);
      setBankDetailsLoading(true);
      setPaymentSettingsLoading(true);

      // Reset previous data
      setBankDetails(null);
      setBillerAddress(null);
      setPaymentSettings(null);
      setPaymentMethod(null);

      const contractResponse = await makeRequest2(
        `/api/app/levy_management/get_contract/${facilityId}/${contractId}`,
        "GET",
      );

      if (!contractResponse.success) {
        toastify("Failed to fetch contract details", "error");
        return;
      }

      const contractData = extractResponseData(contractResponse);

      if (!contractData) {
        toastify("No contract data found", "error");
        return;
      }

      setContractDetails(contractData);

      // Check if levyId exists in the contract
      if (contractData && contractData.levyId) {
        let levy = null;

        if (
          typeof contractData.levyId === "object" &&
          contractData.levyId !== null &&
          contractData.levyId._id
        ) {
          levy = contractData.levyId;
        } else if (
          typeof contractData.levyId === "string" ||
          (typeof contractData.levyId === "object" && contractData.levyId.$oid)
        ) {
          const levyIdToFetch =
            typeof contractData.levyId === "string"
              ? contractData.levyId
              : contractData.levyId.$oid;
          levy = await fetchLevyById(levyIdToFetch);
        } else {
          console.error("Invalid levyId format:", contractData.levyId);
        }

        if (levy) {
          await processLevyPaymentData(levy);
        } else {
          setBillerAddressLoading(false);
          setBankDetailsLoading(false);
          setPaymentSettingsLoading(false);
        }
      } else {
        setBillerAddressLoading(false);
        setBankDetailsLoading(false);
        setPaymentSettingsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching contract details:", error);
      toastify("Error fetching contract details", "error");
      setBillerAddressLoading(false);
      setBankDetailsLoading(false);
      setPaymentSettingsLoading(false);
    } finally {
      setContractLoading(false);
    }
  };

  // Process levy payment data
  const processLevyPaymentData = async (levy) => {
    try {
      const promises = [];

      if (levy.billerAddressId) {
        promises.push(fetchBillerAddress(levy.billerAddressId));
      } else {
        setBillerAddressLoading(false);
      }

      if (levy.bankPayment && levy.bankAccountId) {
        promises.push(fetchBankDetails(levy.bankAccountId));
      } else {
        setBankDetailsLoading(false);
      }

      if (levy.mobilePayment && levy.paymentMethodId) {
        promises.push(fetchMobilePaymentDetails(levy.paymentMethodId));
      } else {
        setPaymentSettingsLoading(false);
      }

      if (promises.length > 0) {
        await Promise.allSettled(promises);
      }
    } catch (error) {
      console.error("Error processing payment configuration:", error);
      toastify("Error processing payment configuration", "error");
      setBillerAddressLoading(false);
      setBankDetailsLoading(false);
      setPaymentSettingsLoading(false);
    }
  };

  // Fetch bank details
  const fetchBankDetails = async (bankAccountId) => {
    try {
      const response = await makeRequest2(
        `${getBankDetailsForLevyURL}/${facilityId}/${bankAccountId}`,
        "GET",
      );

      if (response.success) {
        const bankData = extractResponseData(response);
        setBankDetails(bankData);
        setPaymentMethod("bank");
      } else {
        toastify("Failed to fetch bank details", "error");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      toastify("Error fetching bank details", "error");
    } finally {
      setBankDetailsLoading(false);
    }
  };

  // Fetch mobile payment details
  const fetchMobilePaymentDetails = async (paymentMethodId) => {
    try {
      const response = await makeRequest2(
        `${getFacilityPaymentDetailsURL}/${facilityId}/${paymentMethodId}`,
        "GET",
      );

      if (response.success) {
        const paymentData = extractResponseData(response);
        setPaymentSettings(paymentData);
        setPaymentMethod("mobile");
      } else {
        // toastify("Failed to fetch mobile payment details", "error");
      }
    } catch (error) {
      console.error("Error fetching mobile payment details:", error);
      toastify("Error fetching mobile payment details", "error");
    } finally {
      setPaymentSettingsLoading(false);
    }
  };

  // Fetch biller address
  const fetchBillerAddress = async (billerAddressId) => {
    try {
      const response = await makeRequest2(
        `${getBillerAddressForLevyURL}/${facilityId}/${billerAddressId}`,
        "GET",
      );

      if (response.success) {
        const billerData = extractResponseData(response);
        setBillerAddress(billerData);
      } else {
        toastify("Failed to fetch biller address", "error");
      }
    } catch (error) {
      console.error("Error fetching biller address:", error);
      toastify("Error fetching biller address", "error");
    } finally {
      setBillerAddressLoading(false);
    }
  };

  // Function to mark the invoice as viewed
  const markInvoiceAsViewed = async () => {
    try {
      const response = await makeRequest(
        `${markInvoiceViewed}/${invoiceId}`,
        "PUT",
        {
          facilityId: facilityId,
        },
      );
    } catch (error) {
      console.error("Error marking invoice as viewed:", error);
    }
  };

  // Handle zoom in
  const handleZoomIn = () => {
    if (zoomLevel < MAX_ZOOM) {
      setZoomLevel((prevZoom) => Math.min(prevZoom + ZOOM_STEP, MAX_ZOOM));
    }
  };

  // Handle zoom out
  const handleZoomOut = () => {
    if (zoomLevel > MIN_ZOOM) {
      setZoomLevel((prevZoom) => Math.max(prevZoom - ZOOM_STEP, MIN_ZOOM));
    }
  };

  // Center the invoice after zoom change
  useEffect(() => {
    if (invoiceViewerRef.current) {
      const container = invoiceViewerRef.current;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;

      if (scrollWidth > clientWidth) {
        container.scrollLeft = (scrollWidth - clientWidth) / 2;
      }
    }
  }, [zoomLevel]);

  // Calculate zoom percentage for display
  const zoomPercentage = Math.round(zoomLevel * 100);

  const fetchCompanyDetails = async () => {
    try {
      const response = await makeRequest(
        `/api/public/company_information/${facilityId}`,
        "GET",
      );
      if (response.success) {
        setCompanyDetails(response.data.data);

        if (response.data.logo) {
          const fullLogoUrl = `${process.env.APP_BASE_URL}/${response.data.logo}`;
          setFacilityLogo(fullLogoUrl);
        }
      } else {
        console.error("Failed to fetch company details:", response.message);
      }
    } catch (error) {
      console.error("Error fetching company details:", error);
    }
  };

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);

      const typeParam = type ? `/${type}` : "";
      const response = await makeRequest(
        `/api/public/invoice/${facilityId}/${invoiceId}${typeParam}`,
        "GET",
      );

      if (response.success && response.data) {
        const invoiceData = response.data.data?.data || response.data.data;

        if (invoiceData) {
          setInvoice(invoiceData);
          validateInvoiceType(invoiceData);
        } else {
          setError("Invoice data structure is invalid");
          console.error("Invalid invoice data structure:", response);
        }
      } else {
        console.error("Failed to fetch invoice:", {
          status: response.status,
          message: response.error || response.message,
          facilityId,
          invoiceId,
          type,
        });

        setError(
          response.error || response.message || "Failed to fetch invoice",
        );
      }
    } catch (error) {
      console.error("Error fetching invoice:", {
        message: error.message,
        facilityId,
        invoiceId,
        type,
        stack: error.stack,
      });

      setError(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine invoice type
  const determineInvoiceType = (invoice) => {
    if (!invoice || !invoice.whatFor) return "levy";

    if (invoice.whatFor.invoiceType) {
      const typeValue = invoice.whatFor.invoiceType.toLowerCase();
      if (typeValue === "lease") {
        return "lease";
      } else if (
        typeValue === "contract" ||
        typeValue === "levy" ||
        typeValue === "service"
      ) {
        return "levy";
      }
    }

    return "levy";
  };

  // Validate the invoice type against the URL and redirect if needed
  const validateInvoiceType = (invoiceData) => {
    if (!invoiceData || !invoiceData.whatFor) return;

    const actualType = determineInvoiceType(invoiceData);

    if (!type || type.toLowerCase() !== actualType.toLowerCase()) {
      console.log(
        `Invoice type mismatch. URL: ${type || "none"}, Actual: ${actualType}. Redirecting...`,
      );
      navigate(`/invoice/${facilityId}/${invoiceData._id}/${actualType}`, {
        replace: true,
      });
    }
  };

  const handleDownloadPDF = async () => {
    if (!invoice) return;

    const element = document.getElementById("printable-invoice");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `Invoice_${invoice.invoiceNumber}_${timestamp}.pdf`;

    try {
      setLoading(true);
      console.log("Generating PDF...");

      const cloneElement = element.cloneNode(true);
      cloneElement.style.transform = "none";
      cloneElement.style.position = "absolute";
      cloneElement.style.left = "-9999px";
      document.body.appendChild(cloneElement);

      const canvas = await html2canvas(cloneElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false,
      });

      document.body.removeChild(cloneElement);

      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight, "", "FAST");
      pdf.save(filename);

      console.log("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Receipt viewing functionality (similar to invoice page)
  const viewReceipt = (paymentData) => {
    const allowedTypes = [
      "cash",
      "mpesa-transfer",
      "bank-transfer",
      "cheque",
      "payment",
    ];
    const paymentType = (paymentData.type || "").toLowerCase();

    const isAllowedType = allowedTypes.some(
      (type) =>
        paymentType === type || paymentType.includes(type.replace("-", "")),
    );

    if (!isAllowedType) {
      toastify(
        "Receipt viewing is only available for cash, M-Pesa, bank transfers, and cheque payments",
        "info",
      );
      return;
    }

    navigate(`/facility/levy_management/receipt/${paymentData._id}`, {
      state: {
        receipt: paymentData,
        invoice: invoice,
        billerAddress: billerAddress,
      },
    });
  };

  const actionBodyTemplate = (rowData) => {
    const allowedTypes = [
      "cash",
      "mpesa-transfer",
      "bank-transfer",
      "cheque",
      "payment",
    ];
    const paymentType = (rowData.type || "").toLowerCase();

    const isAllowedType = allowedTypes.some(
      (type) =>
        paymentType === type || paymentType.includes(type.replace("-", "")),
    );

    return (
      <Button
        icon="fas fa-eye"
        className="p-button-sm p-button-text"
        onClick={() => viewReceipt(rowData)}
        disabled={!isAllowedType}
        tooltip={
          isAllowedType
            ? "View Receipt"
            : "Receipt not available for this payment type"
        }
        tooltipOptions={{ position: "left" }}
      />
    );
  };

  // Helper functions
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    const currencyCode = invoice?.currency?.code || "KES";
    return `${currencyCode} ${Number.parseFloat(amount).toFixed(2)}`;
  };

  // Get invoice title
  const getInvoiceTitle = () => {
    if (invoice?.whatFor?.invoiceType?.toLowerCase() === "lease") {
      return "LEASE INVOICE";
    }
    return "INVOICE";
  };

  // Balance calculation methods
  const hasBalanceBroughtForward = () => {
    return (
      invoice?.balanceBroughtForward !== 0 &&
      invoice?.balanceBroughtForward !== null &&
      invoice?.balanceBroughtForward !== undefined
    );
  };

  const isCredit = invoice?.balanceBroughtForward < 0;

  const calculateTotalBalance = () => {
    if (!invoice) return 0;

    const totalAmount = invoice.totalAmount || 0;
    const balanceBroughtForward = invoice.balanceBroughtForward || 0;

    return totalAmount + balanceBroughtForward;
  };

  const calculateTotalDue = () => {
    if (!invoice) return 0;

    const totalBalance = calculateTotalBalance();
    const amountPaid = invoice.amountPaid || 0;

    return totalBalance - amountPaid;
  };

  const isFinalCredit = calculateTotalDue() < 0;

  const formatBalanceBroughtForward = () => {
    if (
      !invoice ||
      !invoice.balanceBroughtForward ||
      invoice.balanceBroughtForward === 0
    ) {
      return formatAmount(0);
    }

    return formatAmount(Math.abs(invoice.balanceBroughtForward));
  };

  // Render loading state
  if (loading) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p>Loading invoice details...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            Please try refreshing the page or contact support if the issue
            persists.
          </p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <div className="alert alert-info" role="alert">
          <h4 className="alert-heading">Invoice Not Found</h4>
          <p>The requested invoice could not be found or has been removed.</p>
          <hr />
          <p className="mb-0">Please check the invoice number and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      {/* Action Button - Enhanced with receipts dialog */}
      <div className={styles.actionCard}>
        <div className={styles.actionCardLeft}>
          <h5 style={{ margin: 0, fontWeight: 500 }}>Invoice Viewer</h5>

          {/* Data Loading Indicator */}
          {contractLoading && (
            <div style={{ display: "inline-block", marginLeft: "20px" }}>
              <span className="text-muted">
                <i className="fas fa-spinner fa-spin me-1"></i>
                Loading contract details...
              </span>
            </div>
          )}
        </div>
        <div className={styles.actionCardRight}>
          {/* View Receipts Button */}
          {invoice?.reconciliationHistory &&
            invoice.reconciliationHistory.length > 0 && (
              <button
                className="btn btn-secondary me-2"
                onClick={() => setShowReceiptsDialog(true)}
              >
                <i className="fas fa-receipt me-1"></i>
                View Receipts
              </button>
            )}

          <button
            className="btn btn-primary"
            onClick={handleDownloadPDF}
            disabled={loading}
          >
            Download Invoice
          </button>
        </div>
      </div>

      {/* Data Status Messages */}
      {allPaymentDataLoaded && invoice?.whatFor?.invoiceType === "Contract" && (
        <div className="mb-3">
          {!billerAddress && (
            <div className="alert alert-warning" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              No biller address configured for this invoice.
            </div>
          )}
          {!bankDetails && !paymentSettings && (
            <div className="alert alert-warning" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              No payment method configured for this invoice.
            </div>
          )}
        </div>
      )}

      {/* Show loading indicators for individual components */}
      {!allPaymentDataLoaded &&
        invoice?.whatFor?.invoiceType === "Contract" && (
          <div className="mb-3">
            {contractLoading && (
              <div className="alert alert-info" role="alert">
                <i className="fas fa-spinner fa-spin me-2"></i>
                Loading contract details...
              </div>
            )}
            {billerAddressLoading && (
              <div className="alert alert-info" role="alert">
                <i className="fas fa-spinner fa-spin me-2"></i>
                Loading biller address...
              </div>
            )}
            {(bankDetailsLoading || paymentSettingsLoading) && (
              <div className="alert alert-info" role="alert">
                <i className="fas fa-spinner fa-spin me-2"></i>
                Loading payment details...
              </div>
            )}
          </div>
        )}

      {/* Receipts Dialog */}
      <Dialog
        header="Payment Receipts"
        visible={showReceiptsDialog}
        style={{ width: "50vw" }}
        onHide={() => setShowReceiptsDialog(false)}
        resizable={false}
      >
        <DataTable
          value={invoice?.reconciliationHistory || []}
          responsiveLayout="scroll"
          emptyMessage="No payment records found"
          className="p-datatable-sm"
          paginator
          rows={5}
          rowsPerPageOptions={[5, 10, 20]}
        >
          <Column
            field="date"
            header="Date"
            body={(rowData) => formatDate(rowData.date)}
            sortable
          />
          <Column
            field="amount"
            header="Amount"
            body={(rowData) => formatAmount(rowData.amount)}
            sortable
          />
          <Column
            field="type"
            header="Type"
            sortable
            body={(rowData) => {
              const paymentType = (rowData.type || "").toLowerCase();
              const allowedTypes = [
                "cash",
                "mpesa-transfer",
                "bank-transfer",
                "cheque",
                "payment",
              ];
              const isAllowedType = allowedTypes.some(
                (type) =>
                  paymentType === type ||
                  paymentType.includes(type.replace("-", "")),
              );

              let displayType = rowData.type || "Unknown";
              if (paymentType === "cash") displayType = "Cash";
              else if (
                paymentType === "mpesa-transfer" ||
                paymentType.includes("mpesa")
              )
                displayType = "M-Pesa";
              else if (
                paymentType === "bank-transfer" ||
                paymentType.includes("bank")
              )
                displayType = "Bank Transfer";
              else if (paymentType === "cheque" || paymentType === "check")
                displayType = "Cheque";
              else if (paymentType === "payment") displayType = "Payment";

              return (
                <div>
                  {displayType}
                  {isAllowedType && (
                    <i
                      className="fas fa-receipt ml-2"
                      style={{
                        color: "#28a745",
                        fontSize: "0.85em",
                        marginLeft: "5px",
                      }}
                      title="Receipt available"
                    />
                  )}
                </div>
              );
            }}
          />
          <Column field="paymentReference" header="Reference" sortable />
          <Column
            field="paymentCompletion"
            header="Status"
            sortable
            body={(rowData) => (
              <span
                className={`badge ${
                  rowData.paymentCompletion === "Completed"
                    ? "bg-success"
                    : rowData.paymentCompletion === "Partial"
                      ? "bg-warning"
                      : rowData.paymentCompletion === "Overpaid"
                        ? "bg-info"
                        : "bg-secondary"
                }`}
              >
                {rowData.paymentCompletion}
              </span>
            )}
          />
          <Column
            body={actionBodyTemplate}
            exportable={false}
            style={{ width: "4rem" }}
          />
        </DataTable>
      </Dialog>

      {/* Zoom Controls */}
      <div className={styles.zoomControls}>
        <button
          className={styles.zoomBtn}
          onClick={handleZoomOut}
          disabled={zoomLevel <= MIN_ZOOM}
          title="Zoom Out"
        >
          -
        </button>
        <div className={styles.zoomLevel}>{zoomPercentage}%</div>
        <button
          className={styles.zoomBtn}
          onClick={handleZoomIn}
          disabled={zoomLevel >= MAX_ZOOM}
          title="Zoom In"
        >
          +
        </button>
      </div>

      {/* Invoice Content in Scrollable Container */}
      <div className={styles.invoiceViewerContainer} ref={invoiceViewerRef}>
        <div
          className={styles.invoiceContainer}
          style={{ transform: `scale(${zoomLevel})` }}
        >
          <div id="printable-invoice" className={styles.invoiceContent}>
            {/* Status banner positioned to match reference image */}
            <div
              className={styles.statusBanner}
              data-status={invoice?.status?.toLowerCase() || "unpaid"}
            ></div>

            <div className={styles.invoiceHeader}>
              <div className={styles.invoiceHeaderContent}>
                <div className={styles.headerLeft}>
                  <h1 className={styles.invoiceTitleInHeader}>INVOICE</h1>
                  {facilityLogo && (
                    <div className={styles.logoContainer}>
                      <img
                        src={facilityLogo || "/placeholder.svg"}
                        alt="Company Logo"
                        className={styles.companyLogo}
                      />
                    </div>
                  )}
                </div>
                <div className={styles.headerDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE NO.</span>
                    <span className={styles.value}>
                      {invoice?.invoiceNumber}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>ACCOUNT NO.</span>
                    <span className={styles.value}>
                      {invoice?.accountNumber}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE DATE</span>
                    <span className={styles.value}>
                      {formatDate(invoice?.issueDate)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>DUE DATE</span>
                    <span className={styles.value}>
                      {formatDate(invoice?.dueDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.invoiceBody}>
              <div className={styles.addressSection}>
                <div className={styles.addressLayout}>
                  <div className={styles.fromSection}>
                    <h2 className={styles.sectionTitle}>FROM</h2>
                    <div className={styles.addressContent}>
                      {billerAddress ? (
                        <>
                          {billerAddress.companyName && (
                            <div>{billerAddress.companyName}</div>
                          )}
                          {billerAddress.address && (
                            <div>{billerAddress.address}</div>
                          )}
                          {billerAddress.postalCode && (
                            <div>{billerAddress.postalCode}</div>
                          )}
                          {(billerAddress.city || billerAddress.country) && (
                            <div>
                              {billerAddress.city}
                              {billerAddress.city && billerAddress.country
                                ? ", "
                                : ""}
                              {billerAddress.country}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div>{companyDetails?.name || "Company Name"}</div>
                          <div>
                            {companyDetails?.address || "Company Address"}
                          </div>
                          <div>{companyDetails?.country || "Country"}</div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className={styles.billToSection}>
                    <h2 className={styles.sectionTitle}>BILL TO</h2>
                    <div className={styles.addressContent}>
                      {invoice?.client ? (
                        <div>
                          {`${invoice.client.firstName || ""} ${invoice.client.lastName || ""}`.trim() ||
                            "Client"}
                        </div>
                      ) : (
                        <div>Client Information Unavailable</div>
                      )}
                      <div>Unit: {invoice?.unit?.name || "N/A"}</div>
                    </div>
                  </div>
                  <div className={styles.totalSection}>
                    <h2 className={styles.sectionTitle}>TOTAL DUE</h2>
                    <div
                      className={`${styles.totalAmount} ${isFinalCredit ? styles.creditAmount : ""}`}
                    >
                      {isFinalCredit
                        ? formatAmount(-Math.abs(calculateTotalDue()))
                        : formatAmount(calculateTotalDue())}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.itemsGrid}>
                <div className={styles.gridHeader}>
                  <span className={styles.columnHeader}>DESCRIPTION</span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>
                    UNIT PRICE
                  </span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>
                    QTY
                  </span>
                  <span className={`${styles.columnHeader} ${styles.right}`}>
                    AMOUNT
                  </span>
                </div>

                {invoice?.items?.map((item, index) => (
                  <div key={index} className={styles.gridRow}>
                    <span className={styles.value}>{item.description}</span>
                    <span className={`${styles.value} ${styles.right}`}>
                      {formatAmount(item.unitPrice)}
                    </span>
                    <span className={`${styles.value} ${styles.right}`}>
                      {item.quantity}
                    </span>
                    <span className={`${styles.value} ${styles.right}`}>
                      {formatAmount(item.quantity * item.unitPrice)}
                    </span>
                  </div>
                ))}

                <div className={styles.calculations}>
                  <div className={styles.calculationsGrid}>
                    {/* Invoice Calculation Section */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>SUBTOTAL</span>
                      <span className={styles.calcValue}>
                        {formatAmount(invoice?.subTotal)}
                      </span>
                    </div>
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>
                        TAX (
                        {((invoice?.tax / invoice?.subTotal) * 100).toFixed(
                          0,
                        ) || 0}
                        %)
                      </span>
                      <span className={styles.calcValue}>
                        {formatAmount(invoice?.tax)}
                      </span>
                    </div>
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>INVOICE TOTAL</span>
                      <span className={styles.calcValue}>
                        {formatAmount(invoice?.totalAmount)}
                      </span>
                    </div>

                    {/* Divider */}
                    <div className={styles.divider}></div>

                    {/* Balance Brought Forward */}
                    {hasBalanceBroughtForward() && (
                      <div className={styles.calculationsRow}>
                        <span className={styles.calcLabel}>
                          BALANCE B/FORWARD
                        </span>
                        <span
                          className={`${styles.calcValue} ${isCredit ? styles.creditAmount : styles.debitAmount}`}
                        >
                          {isCredit
                            ? "(" + formatBalanceBroughtForward() + ")"
                            : formatBalanceBroughtForward()}
                        </span>
                      </div>
                    )}

                    {/* Total Balance */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>AMOUNT PAYABLE</span>
                      <span className={styles.calcValue}>
                        {formatAmount(calculateTotalBalance())}
                      </span>
                    </div>

                    {/* Divider */}
                    <div className={styles.divider}></div>

                    {/* Amount Paid */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>PAYMENT</span>
                      <span className={styles.calcValue}>
                        {formatAmount(invoice?.amountPaid)}
                      </span>
                    </div>

                    {/* Total Due */}
                    <div
                      className={`${styles.calculationsRow} ${styles.balance}`}
                    >
                      <span className={styles.calcLabel}>TOTAL DUE</span>
                      <span
                        className={`${styles.calcValue} ${isFinalCredit ? styles.creditAmount : styles.debitAmount}`}
                      >
                        {isFinalCredit
                          ? formatAmount(-Math.abs(calculateTotalDue()))
                          : formatAmount(calculateTotalDue())}
                      </span>
                    </div>
                  </div>

                  <div className={styles.totalRow}>
                    <span className={styles.totalLabel}>TOTAL DUE</span>
                    <span
                      className={`${styles.totalValue} ${isFinalCredit ? styles.creditAmount : styles.debitAmount}`}
                    >
                      {isFinalCredit
                        ? "(" +
                          formatAmount(Math.abs(calculateTotalDue())) +
                          ")"
                        : formatAmount(calculateTotalDue())}
                    </span>
                  </div>
                </div>
              </div>

              {/* Enhanced Payment Details Footer - Now with full contract support */}
              {allPaymentDataLoaded ? (
                // Show payment details only when all data is loaded
                paymentMethod === "mobile" && paymentSettings ? (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row align-items-center">
                      <div className="col-sm-4">
                        <div
                          className={styles.paymentDetails}
                          style={{ marginBottom: 0 }}
                        >
                          <p
                            style={{
                              fontSize: "12px",
                              fontWeight: "bold",
                              marginBottom: "4px",
                            }}
                          >
                            Payment Details:
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Bank:</strong> Mpesa
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Paybill:</strong>{" "}
                            {paymentSettings?.shortCode || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="col-sm-5 text-center">
                        <img
                          src="/signature.png"
                          alt="Digital Signature"
                          style={{
                            width: "120px",
                            height: "45px",
                            objectFit: "contain",
                          }}
                        />
                        <p
                          style={{
                            textAlign: "center",
                            marginTop: "2px",
                            marginBottom: "2px",
                            fontSize: "10px",
                          }}
                        >
                          ___________________________
                        </p>
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "9px",
                            marginBottom: 0,
                          }}
                        >
                          Authorized Signature
                        </p>
                      </div>

                      {/* eTims QR Code */}
                      {invoice?.ticketUrl && (
                        <div className="col-sm-3 text-center">
                          <EtimsQRCode
                            ticketUrl={invoice.ticketUrl}
                            size={80}
                          />
                        </div>
                      )}
                    </div>

                    <div className="row">
                      <div className="col-12 pt-1">
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "11px",
                            marginBottom: 0,
                          }}
                        >
                          To complete your payment, use paybill
                          <strong>
                            {" "}
                            {paymentSettings?.shortCode || "N/A"}{" "}
                          </strong>
                          with account number
                          <strong> {invoice?.accountNumber || "N/A"}</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : paymentMethod === "bank" && bankDetails ? (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row">
                      <div className="col-12 text-center pb-2">
                        <h6
                          className={styles.detailsTitle}
                          style={{
                            margin: "0 auto",
                            fontWeight: "bold",
                            fontSize: "12px",
                          }}
                        >
                          PAYMENT DETAILS
                        </h6>
                      </div>
                    </div>

                    <div className="row align-items-center">
                      <div className="col-sm-3">
                        <div
                          className={styles.paymentDetails}
                          style={{ marginBottom: 0 }}
                        >
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Bank:</strong>{" "}
                            {bankDetails?.bankName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Account:</strong>{" "}
                            {bankDetails?.accountName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Acc No:</strong>{" "}
                            {bankDetails?.accountNumber || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="col-sm-3">
                        <div
                          className={styles.additionalDetails}
                          style={{ marginBottom: 0 }}
                        >
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Branch:</strong>{" "}
                            {bankDetails?.branchName || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Code:</strong>{" "}
                            {bankDetails?.branchCode || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Swift:</strong>{" "}
                            {bankDetails?.bankCode || "N/A"}
                          </p>
                        </div>
                      </div>

                      <div className="col-sm-4 text-center">
                        <img
                          src="/signature.png"
                          alt="Digital Signature"
                          style={{
                            width: "120px",
                            height: "45px",
                            objectFit: "contain",
                          }}
                        />
                        <p
                          style={{
                            marginTop: "0px",
                            marginBottom: "2px",
                            fontSize: "10px",
                          }}
                        >
                          ___________________________
                        </p>
                        <p style={{ fontSize: "9px", marginBottom: 0 }}>
                          Authorized Signature
                        </p>
                      </div>

                      {/* eTims QR Code */}
                      {invoice?.ticketUrl && (
                        <div className="col-sm-2 text-center">
                          <EtimsQRCode
                            ticketUrl={invoice.ticketUrl}
                            size={80}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Default footer - Enhanced fallback logic
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f0f0f0",
                      padding: "12px 20px",
                      marginLeft: "-20px",
                      marginRight: "-20px",
                      marginBottom: "-20px",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                    }}
                  >
                    <div className="row align-items-center">
                      <div className="col-sm-4">
                        <div
                          className={styles.paymentDetails}
                          style={{ marginBottom: 0 }}
                        >
                          <p
                            style={{
                              fontSize: "12px",
                              fontWeight: "bold",
                              marginBottom: "4px",
                            }}
                          >
                            Payment Details:
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Bank:</strong> Mpesa
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: "2px" }}>
                            <strong>Paybill:</strong>{" "}
                            {invoice?.paymentDetails?.paymentMethod?.split(
                              " - ",
                            )[1] || "N/A"}
                          </p>
                          <p style={{ fontSize: "11px", marginBottom: 0 }}>
                            <strong>Acc No:</strong> {invoice?.accountNumber}
                          </p>
                        </div>
                      </div>

                      <div className="col-sm-5 text-center">
                        <p
                          style={{
                            marginTop: "5px",
                            marginBottom: "2px",
                            fontSize: "10px",
                          }}
                        >
                          ___________________________
                        </p>
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "9px",
                            marginBottom: 0,
                          }}
                        >
                          Authorized Signature
                        </p>
                      </div>

                      {/* eTims QR Code */}
                      {invoice?.ticketUrl && (
                        <div className="col-sm-3 text-center">
                          <EtimsQRCode
                            ticketUrl={invoice.ticketUrl}
                            size={80}
                          />
                        </div>
                      )}
                    </div>
                    <div className="row">
                      <div className="col-12 pt-1">
                        <p
                          style={{
                            textAlign: "center",
                            fontSize: "11px",
                            marginBottom: 0,
                          }}
                        >
                          To complete your payment, use paybill
                          <strong>
                            {" "}
                            {invoice?.paymentDetails?.paymentMethod?.split(
                              " - ",
                            )[1] || "N/A"}{" "}
                          </strong>
                          with account number
                          <strong> {invoice?.accountNumber || "N/A"}</strong>.
                        </p>
                      </div>
                    </div>
                  </div>
                )
              ) : (
                // Loading state for payment details
                invoice?.whatFor?.invoiceType === "Contract" && (
                  <div
                    className={styles.footer}
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderTop: "2px solid #dee2e6",
                    }}
                  >
                    <div className="row px-5 py-4">
                      <div className="col-12 text-center">
                        <div className={styles.loadingPaymentDetails}>
                          <div
                            className="spinner-border text-primary mb-3"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h5 className="text-muted">
                            Loading Payment Details
                          </h5>
                          <p className="text-muted mb-0">
                            Please wait while we fetch the payment
                            information...
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversalInvoicePage;
